import React, { useEffect, useState } from "react";
import { useGetTrendViewQuery } from "../../../../redux/service/modules/dashboard";
import { useSelector } from "react-redux";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";
import { Box } from "@mui/material";
import Loader from "../../../../components/common/loader";
import NoDataAvailable from "../../../../components/common/NoDataAvailable";
import { CustomXAxisTick } from "./CustomXAxisTick";
import { CustomTooltip } from "./CustomTooltip";
import { useGetActiveTiersQuery } from "../../../../redux/service/modules/sales";
import { useResponsiveStyles } from "../../../../styles/useResponsiveStyles";

const transformDataToDesiredFormat = (data) => {
    const transformedArray = [];

    // Iterate over each category
    Object.keys(data).forEach((category) => {
        // Iterate over each quarter
        Object.keys(data[category]).forEach((quarter) => {
            const values = data[category][quarter];
            const formattedQuarter = quarter
                .replace("-", "/")
                .replace("Q", "Q"); // Format quarter

            // Find if the entry for this quarter already exists
            const entry = transformedArray.find(
                (item) =>
                    item.quarter === formattedQuarter &&
                    item.category === category
            );

            // If the entry for the quarter doesn't exist, create it
            if (!entry) {
                transformedArray.push({
                    quarter: `${formattedQuarter}|${category}`, // Use the formatted quarter
                    category: category,
                    ...values, // Spread the values into the entry
                });
            } else {
                // If it exists, sum the values
                Object.keys(values).forEach((key) => {
                    entry[key] = (entry[key] || 0) + values[key];
                });
            }
        });
    });

    // Sort the transformed array by category and then by quarter
    transformedArray.sort((a, b) => {
        const [qA, yearA] = a.quarter
            .split("/")
            .map((part) => parseInt(part.replace("Q", "")));
        const [qB, yearB] = b.quarter
            .split("/")
            .map((part) => parseInt(part.replace("Q", "")));
        if (a.category === b.category) {
            return yearA === yearB ? qA - qB : yearA - yearB;
        }
        return a.category.localeCompare(b.category);
    });

    return transformedArray;
};

export const Trend = () => {
    const dateRange = useSelector((state) => state.date);
    const { data, isFetching, error } = useGetTrendViewQuery(dateRange);
    const { getFontSize } = useResponsiveStyles();
    const [chartData, setChartData] = useState([]);
    const { data: activeTiers } = useGetActiveTiersQuery({
        include_clients: false,
    });

    useEffect(() => {
        if (data) {
            const transformed = transformDataToDesiredFormat(data);
            setChartData(transformed);
        }
    }, [data]);

    let staticContent;
    if (isFetching) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (
        chartData.length === 0 ||
        (chartData.length > 0 &&
            chartData?.every((row) =>
                Object.keys(row).every(
                    (key) => ["name"].includes(key) || row[key] === 0
                )
            ))
    ) {
        staticContent = <NoDataAvailable />;
    }
    const sortedTierData = activeTiers
        ?.slice()
        .sort((a, b) => a.tier_name.localeCompare(b.tier_name));

    return (
        <Box>
            {staticContent ? (
                <div style={styles.staticContent}>{staticContent}</div>
            ) : (
                <Box className="h-[calc(100vh-9rem)] min-h-[400px] pb-8">
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        className={"mt-2"}
                    >
                        <BarChart
                            data={chartData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="quarter"
                                tick={(props) => <CustomXAxisTick {...props} />}
                                interval={0}
                                angle={-45}
                                // textAnchor="end"
                            />
                            <YAxis />
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{ fill: "transparent" }}
                            />
                            <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                wrapperStyle={{
                                    paddingTop: "100px",
                                    ...getFontSize("body1"),
                                }}
                                itemStyle={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                                formatter={(value) => {
                                    return (
                                        <span
                                            style={{
                                                color: "black",
                                                marginRight: 15,
                                            }}
                                        >
                                            {value
                                                .split("_")
                                                .map(
                                                    (word) =>
                                                        word
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                        word
                                                            .slice(1)
                                                            .toLowerCase()
                                                )
                                                .join(" ")}
                                        </span>
                                    );
                                }}
                            />
                            {sortedTierData?.map((tier) => (
                                <Bar
                                    key={tier.tier_name}
                                    dataKey={tier.tier_name}
                                    stackId="a"
                                    fill={tier.tier_color || "#000000"}
                                    name={tier.tier_name}
                                >
                                    {/* <LabelList
                                    dataKey={tier.tier_name}
                                    position="inside"
                                    style={{
                                        fontWeight: "bold",
                                        color: "#231F20",
                                    }}
                                    formatter={(value) =>
                                        value > 0 ? value : null
                                    }
                                /> */}
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
};

const styles = {
    staticContent: {
        height: "90%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};
